import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useNotification } from '../../context/NotificationContext';
import './Auth.css';

const API_URL = process.env.REACT_APP_API_URL;

const EyeIcon = ({ off }) => (
  <svg className="auth-eye" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {off ? (
      <>
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
        <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </>
    ) : (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </>
    )}
  </svg>
);

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Parola doğru ama e-posta doğrulanmamışsa "tekrar gönder" seçeneği gösterilir
  const [needsVerification, setNeedsVerification] = useState(false);
  // Art arda tıklamayla sunucudaki saatlik gönderim sınırı (429) tüketilmesin
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setNeedsVerification(false);
    try {
      const res = await fetch(`${API_URL}/api/users/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.code === 'EMAIL_NOT_VERIFIED') setNeedsVerification(true);
        showNotification(data.error || 'Giriş başarısız!', 'error');
        return;
      }
      localStorage.setItem('token', data.token);
      showNotification('Başarıyla giriş yaptınız!', 'success');
      navigate('/groups');
    } catch (err) {
      console.error('Giriş hatası:', err);
      showNotification('Sunucuya bağlanılamadı.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      showNotification('Lütfen e-posta adresinizi girin.', 'error');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/users/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) {
        showNotification(data.error || 'Parola sıfırlama başarısız!', 'error');
        return;
      }
      showNotification(data.message || 'Bu e-posta kayıtlıysa, parola sıfırlama bağlantısı gönderildi.', 'success');
    } catch (err) {
      console.error('Parola sıfırlama hatası:', err);
      showNotification('Sunucuya bağlanılamadı.', 'error');
    }
  };

  const handleResendVerification = async () => {
    if (isResending || resendCooldown > 0) return;
    if (!email) {
      showNotification('Lütfen e-posta adresinizi girin.', 'error');
      return;
    }
    setIsResending(true);
    try {
      const res = await fetch(`${API_URL}/api/users/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      showNotification(data.message || data.error, res.ok ? 'success' : 'error');
      if (res.ok) setResendCooldown(60);
    } catch (err) {
      console.error('Yeniden gönderme hatası:', err);
      showNotification('Sunucuya bağlanılamadı.', 'error');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="auth-page">
      {/* Sol bilgi alanı (yalnızca geniş ekranlarda) */}
      <aside className="auth-aside" aria-hidden="true">
        <span className="auth-aside-brand">EduConnect</span>
        <div>
          <h2>Kaldığınız yerden <em>devam edin.</em></h2>
          <p>Gruplarınız, sohbetleriniz ve paylaştığınız kaynaklar giriş yaptığınızda sizi bekliyor.</p>
          <ul>
            <li>Çalışma gruplarınız</li>
            <li>Grup ve birebir sohbetler</li>
            <li>Kaynak havuzu ve takvim</li>
          </ul>
        </div>
      </aside>

      <main className="auth-main">
        <div className="auth">
          <span className="auth-kicker">Giriş</span>
          <h1>Tekrar hoş geldiniz</h1>
          <p className="auth-sub">Hesabınıza giriş yapmak için bilgilerinizi girin.</p>

          <form className="auth-form" onSubmit={handleLogin}>
            <label className="auth-field">
              <span className="auth-label">E-posta</span>
              <input
                type="email"
                className="auth-input"
                placeholder="ornek@mail.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setNeedsVerification(false);
                }}
                autoComplete="email"
                required
              />
            </label>

            <div className="auth-field">
              <label className="auth-label" htmlFor="login-password">Parola</label>
              <span className="auth-password">
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  className="auth-input"
                  placeholder="Parolanız"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className="auth-toggle"
                  onMouseDown={(e) => e.preventDefault()} /* imleç alanda kalsın */
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Parolayı gizle' : 'Parolayı göster'}
                  aria-pressed={showPassword}
                  title={showPassword ? 'Parolayı gizle' : 'Parolayı göster'}
                >
                  <EyeIcon off={showPassword} />
                </button>
              </span>
            </div>

            <div className="auth-row">
              <button type="button" className="auth-link" onClick={handleForgotPassword}>
                Şifremi unuttum
              </button>
            </div>

            {needsVerification && (
              <div className="auth-row">
                <button
                  type="button"
                  className="auth-link"
                  onClick={handleResendVerification}
                  disabled={isResending || resendCooldown > 0}
                >
                  {isResending
                    ? 'Gönderiliyor...'
                    : resendCooldown > 0
                    ? `Tekrar göndermek için ${resendCooldown} sn bekleyin`
                    : 'Doğrulama e-postasını tekrar gönder'}
                </button>
              </div>
            )}

            <button className="auth-button" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Giriş yapılıyor...' : 'Giriş yap'}
            </button>
          </form>

          <p className="auth-footer">
            Hesabınız yok mu? <Link to="/register">Kayıt ol</Link>
          </p>
        </div>
      </main>
    </div>
  );
}

export default Login;